import type { Act, Scene } from '../types';

export interface OrderedScene extends Scene {
    actId: string | null;
    actName: string | null;
    actIndex: number;
    sceneIndex: number;
    showOrder: number;
}

export interface SceneScopedSection<T> {
    act: Act;
    items: T[];
}

export interface SceneOrderOptions {
    includePreset?: boolean;
    includeOrphans?: boolean;
}

interface SceneScopedItem {
    sceneId?: string | null;
    sortOrder?: number | null;
}

export interface SceneScopedSortOptions<T> {
    getSceneId?: (item: T) => string | null | undefined;
    sortWithinScene?: (left: T, right: T) => number;
}

export interface BuildSceneScopedSectionsInput<T> extends SceneScopedSortOptions<T> {
    items: T[];
    acts: Act[];
    scenes: Scene[];
    includePreset?: boolean;
}

const DEFAULT_OPTIONS = {
    includePreset: false,
    includeOrphans: true,
};

export function getOrderedScenes(
    acts: Act[],
    scenes: Scene[],
    options?: SceneOrderOptions
): OrderedScene[] {
    const resolvedOptions = { ...DEFAULT_OPTIONS, ...options };
    const sceneById = new Map(
        scenes
            .filter(scene => !scene.deleted)
            .map(scene => [scene.id, scene] as const)
    );
    const excludedPresetSceneIds = resolvedOptions.includePreset
        ? new Set<string>()
        : getPresetSceneIds(acts);
    const activeActSceneIds = getActiveActSceneIds(acts, resolvedOptions.includePreset);
    const excludedDeletedActSceneIds = getDeletedActOnlySceneIds(acts, activeActSceneIds);
    const seenSceneIds = new Set<string>();
    const orderedScenes: OrderedScene[] = [];

    getActiveActs(acts, resolvedOptions.includePreset).forEach((act, actIndex) => {
        let sceneIndex = 0;

        for (const sceneId of act.sceneIds || []) {
            const scene = sceneById.get(sceneId);
            if (!scene || seenSceneIds.has(sceneId)) continue;

            seenSceneIds.add(sceneId);
            orderedScenes.push({
                ...scene,
                actId: act.id,
                actName: act.name,
                actIndex,
                sceneIndex,
                showOrder: orderedScenes.length,
            });
            sceneIndex += 1;
        }
    });

    if (!resolvedOptions.includeOrphans) {
        return orderedScenes;
    }

    let orphanIndex = 0;
    for (const scene of scenes) {
        if (
            scene.deleted
            || seenSceneIds.has(scene.id)
            || excludedPresetSceneIds.has(scene.id)
            || excludedDeletedActSceneIds.has(scene.id)
        ) {
            continue;
        }

        orderedScenes.push({
            ...scene,
            actId: null,
            actName: null,
            actIndex: -1,
            sceneIndex: orphanIndex,
            showOrder: orderedScenes.length,
        });
        orphanIndex += 1;
    }

    return orderedScenes;
}

export function getOrderedSceneIds(
    acts: Act[],
    scenes: Scene[],
    options?: SceneOrderOptions
): string[] {
    return getOrderedScenes(acts, scenes, options).map(scene => scene.id);
}

export function buildSceneOrderIndex(
    acts: Act[],
    scenes: Scene[],
    options?: SceneOrderOptions
): Map<string, number> {
    return new Map(
        getOrderedScenes(acts, scenes, options).map(scene => [scene.id, scene.showOrder] as const)
    );
}

export function compareSceneIdsByShowOrder(
    sceneOrderIndex: Map<string, number>,
    leftSceneId: string | null | undefined,
    rightSceneId: string | null | undefined
): number {
    const leftOrder = getSceneOrder(sceneOrderIndex, leftSceneId);
    const rightOrder = getSceneOrder(sceneOrderIndex, rightSceneId);

    if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
    }

    return String(leftSceneId ?? '').localeCompare(String(rightSceneId ?? ''));
}

export function orderScenesByShowStructure(
    acts: Act[],
    scenes: Scene[],
    options?: SceneOrderOptions
): OrderedScene[] {
    return getOrderedScenes(acts, scenes, options);
}

export function sortSceneScopedItems<T>(
    items: T[],
    acts: Act[],
    scenes: Scene[],
    options?: SceneScopedSortOptions<T>
): T[] {
    const sceneOrderIndex = buildSceneOrderIndex(acts, scenes);
    const getSceneId = options?.getSceneId ?? getDefaultSceneId<T>;
    const sortWithinScene = options?.sortWithinScene ?? defaultSortWithinScene<T>;

    return items
        .map((item, originalIndex) => ({ item, originalIndex }))
        .toSorted((left, right) => {
            const sceneComparison = compareSceneIdsByShowOrder(
                sceneOrderIndex,
                getSceneId(left.item),
                getSceneId(right.item)
            );
            if (sceneComparison !== 0) return sceneComparison;

            const sortOrderComparison = sortWithinScene(left.item, right.item);
            if (sortOrderComparison !== 0) return sortOrderComparison;

            return left.originalIndex - right.originalIndex;
        })
        .map(({ item }) => item);
}

export function buildSceneScopedSections<T>(
    input: BuildSceneScopedSectionsInput<T>
): {
    sections: SceneScopedSection<T>[];
    unassignedItems: T[];
} {
    const includePreset = input.includePreset ?? false;
    const getSceneId = input.getSceneId ?? getDefaultSceneId<T>;
    const orderedScenes = getOrderedScenes(input.acts, input.scenes, {
        includePreset,
        includeOrphans: false,
    });
    const sortedItems = sortSceneScopedItems(input.items, input.acts, input.scenes, {
        getSceneId,
        sortWithinScene: input.sortWithinScene,
    });
    const sceneIdsInActiveActs = new Set(orderedScenes.map(scene => scene.id));
    const itemsBySceneId = new Map<string, T[]>();
    const unassignedItems = input.items.filter(item => {
        const sceneId = getSceneId(item);
        return !sceneId || !sceneIdsInActiveActs.has(sceneId);
    });

    for (const item of sortedItems) {
        const sceneId = getSceneId(item);
        if (!sceneId || !sceneIdsInActiveActs.has(sceneId)) {
            continue;
        }

        const sceneItems = itemsBySceneId.get(sceneId) || [];
        sceneItems.push(item);
        itemsBySceneId.set(sceneId, sceneItems);
    }

    const activeActById = new Map(
        getActiveActs(input.acts, includePreset).map(act => [act.id, act] as const)
    );
    const sectionsByActId = new Map<string, SceneScopedSection<T>>();
    for (const scene of orderedScenes) {
        if (!scene.actId) continue;

        const sceneItems = itemsBySceneId.get(scene.id);
        if (!sceneItems?.length) continue;
        const act = activeActById.get(scene.actId);
        if (!act) continue;

        const section = sectionsByActId.get(scene.actId) || {
            act,
            items: [],
        };

        section.items.push(...sceneItems);
        sectionsByActId.set(scene.actId, section);
    }

    return {
        sections: Array.from(sectionsByActId.values()),
        unassignedItems,
    };
}

function getActiveActs(acts: Act[], includePreset: boolean): Act[] {
    return acts.filter(act => !act.deleted && (includePreset || !act.isPreset));
}

function getActiveActSceneIds(acts: Act[], includePreset: boolean): Set<string> {
    const sceneIds = new Set<string>();
    for (const act of getActiveActs(acts, includePreset)) {
        for (const sceneId of act.sceneIds || []) {
            sceneIds.add(sceneId);
        }
    }
    return sceneIds;
}

function getPresetSceneIds(acts: Act[]): Set<string> {
    const sceneIds = new Set<string>();
    for (const act of acts) {
        if (act.deleted || !act.isPreset) continue;
        for (const sceneId of act.sceneIds || []) {
            sceneIds.add(sceneId);
        }
    }
    return sceneIds;
}

function getDeletedActOnlySceneIds(acts: Act[], activeActSceneIds: Set<string>): Set<string> {
    const sceneIds = new Set<string>();
    for (const act of acts) {
        if (!act.deleted) continue;
        for (const sceneId of act.sceneIds || []) {
            if (!activeActSceneIds.has(sceneId)) {
                sceneIds.add(sceneId);
            }
        }
    }
    return sceneIds;
}

function getSceneOrder(sceneOrderIndex: Map<string, number>, sceneId: string | null | undefined): number {
    if (!sceneId) return Number.POSITIVE_INFINITY;
    return sceneOrderIndex.get(sceneId) ?? Number.POSITIVE_INFINITY;
}

function getDefaultSceneId<T>(item: T): string | null | undefined {
    return (item as SceneScopedItem).sceneId;
}

function defaultSortWithinScene<T>(left: T, right: T): number {
    return getSortOrder(left) - getSortOrder(right);
}

function getSortOrder(item: unknown): number {
    if (!item || typeof item !== 'object') return 0;
    return (item as SceneScopedItem).sortOrder ?? 0;
}
